import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { EmptyStateComponent } from 'src/app/shared/components/common/empty-state/empty-state.component';
import { SkeletonDirective } from 'src/app/shared/directives/skeleton/skeleton.directive';
import { IconInduxtryComponent } from 'src/app/shared/components/common/icon-induxtry/icon-induxtry.component';
import { Alert } from '../../models/alert.model';
import { AlertsApiService } from '../../services/alerts-api.service';
import { AlertFormDialogComponent } from '../alert-form-dialog/alert-form-dialog.component';

type AlertFilterKey = 'name' | 'target' | 'when' | 'people' | 'status';

const WEEKDAY_LABELS: Record<number, string> = {
  0: 'Domingo',
  1: 'Segunda',
  2: 'Terça',
  3: 'Quarta',
  4: 'Quinta',
  5: 'Sexta',
  6: 'Sábado',
};

const WEEKDAY_SHORT_LABELS: Record<number, string> = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb',
};

const WEEKDAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const emptyFilters = (): Record<AlertFilterKey, Set<string>> => ({
  name: new Set(),
  target: new Set(),
  when: new Set(),
  people: new Set(),
  status: new Set(),
});

@Component({
  selector: 'app-alerts-panel',
  templateUrl: './alerts-panel.component.html',
  styleUrls: ['./alerts-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmptyStateComponent,
    IconInduxtryComponent,
    SkeletonDirective,
    MatTooltipModule,
    MatIconModule,
    MatMenuModule,
  ],
})
export class AlertsPanelComponent implements OnInit {
  private readonly alertsApi = inject(AlertsApiService);
  private readonly dialog = inject(MatDialog);
  private readonly toastr = inject(ToastrService);

  readonly alerts = signal<Alert[]>([]);
  readonly isLoading = signal(true);
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly filters = signal(emptyFilters());

  readonly filteredAlerts = computed(() => {
    const selected = this.filters();
    return this.alerts().filter((alert) =>
      this.matchesFilter(selected.name, [alert.name])
      && this.matchesFilter(selected.target, this.targetNameList(alert))
      && this.matchesFilter(selected.when, [this.whenKindLabel(alert)])
      && this.matchesFilter(selected.people, this.recipientList(alert))
      && this.matchesFilter(selected.status, [this.statusLabel(alert)])
    );
  });

  readonly nameFilterOptions = computed(() => this.uniqueSorted(this.alerts().map((alert) => alert.name)));
  readonly targetFilterOptions = computed(() => this.uniqueSorted(this.alerts().flatMap((alert) => this.targetNameList(alert))));
  readonly whenFilterOptions = computed(() => this.uniqueSorted(this.alerts().map((alert) => this.whenKindLabel(alert))));
  readonly peopleFilterOptions = computed(() => this.uniqueSorted(this.alerts().flatMap((alert) => this.recipientList(alert))));
  readonly statusFilterOptions = computed(() => this.uniqueSorted(this.alerts().map((alert) => this.statusLabel(alert))));

  ngOnInit(): void {
    this.load();
  }

  openCreate(): void {
    this.dialog.open(AlertFormDialogComponent, {
      width: '880px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      panelClass: 'alert-form-dialog',
      data: {},
    })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.load();
      });
  }

  openEdit(alert: Alert): void {
    this.dialog.open(AlertFormDialogComponent, {
      width: '880px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      panelClass: 'alert-form-dialog',
      data: { alert },
    })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.load();
      });
  }

  isEnabled(alert: Alert): boolean {
    return alert.enabled !== false;
  }

  statusLabel(alert: Alert): string {
    return this.isEnabled(alert) ? 'Ativo' : 'Inativo';
  }

  hasFilter(key: AlertFilterKey): boolean {
    return this.filters()[key].size > 0;
  }

  isFilterSelected(key: AlertFilterKey, value: string): boolean {
    return this.filters()[key].has(value);
  }

  toggleFilter(key: AlertFilterKey, value: string): void {
    this.filters.update((current) => {
      const next = new Set(current[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...current, [key]: next };
    });
  }

  clearFilter(key: AlertFilterKey): void {
    this.filters.update((current) => ({ ...current, [key]: new Set() }));
  }

  keepMenuOpen(event: Event): void {
    event.stopPropagation();
  }

  targetNames(alert: Alert): string {
    const names = this.targetNameList(alert);
    if (!names.length) return alert.scope_name;
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  targetTitle(alert: Alert): string {
    const names = this.targetNameList(alert);
    return names.length ? names.join(', ') : (alert.scope_name ?? '');
  }

  isTargetAbbreviated(alert: Alert): boolean {
    return this.targetNameList(alert).length > 2;
  }

  recipientLabel(alert: Alert): string {
    const emails = alert.recipients?.map((recipient) => recipient.email) ?? [];
    if (emails.length <= 1) return emails[0] ?? '—';
    return `${emails[0]} +${emails.length - 1}`;
  }

  recipientTitle(alert: Alert): string {
    return (alert.recipients ?? []).map((recipient) => recipient.email).join(', ');
  }

  isRecipientAbbreviated(alert: Alert): boolean {
    return (alert.recipients?.length ?? 0) > 1;
  }

  whenKindLabel(alert: Alert): string {
    return alert.kind === 'freshness' ? 'Freshness' : this.triggerLabel(alert);
  }

  triggerLabel(alert: Alert): string {
    const labels: Record<string, string> = {
      execution_created: 'Nova execução',
      execution_completed: 'Concluída',
      execution_failed: 'Falha',
    };
    return (alert.events ?? []).map((event) => labels[event] ?? event).join(', ');
  }

  freshnessScheduleLabel(alert: Alert): string {
    const frequency = alert.freshness?.frequency === 'weekly'
      ? 'Semanal'
      : alert.freshness?.frequency === 'monthly'
        ? 'Mensal'
        : 'Diário';
    const time = (alert.freshness?.deadline_time ?? '').slice(0, 5);
    const parts = [frequency];

    if (alert.freshness?.frequency === 'weekly') {
      parts.push(WEEKDAY_LABELS[alert.freshness.weekday ?? 5] ?? '');
    } else if (alert.freshness?.frequency === 'monthly') {
      parts.push(`dia ${alert.freshness.day_of_month ?? 1}`);
    } else {
      const daysLabel = this.dailyWeekdaysLabel(alert.freshness?.weekdays);
      if (daysLabel) parts.push(daysLabel);
    }

    if (time) parts.push(time);
    return parts.filter(Boolean).join(' · ');
  }

  private dailyWeekdaysLabel(weekdays?: number[]): string {
    if (!weekdays?.length) return '';
    const unique = [...new Set(weekdays.filter((day) => day >= 0 && day <= 6))];
    if (unique.length === 7) return '';
    const selected = new Set(unique);
    const ordered = WEEKDAY_DISPLAY_ORDER.filter((day) => selected.has(day));
    const names = ordered.map((day) => WEEKDAY_SHORT_LABELS[day] ?? '');
    if (names.length >= 2 && this.areConsecutiveWeekdays(ordered)) {
      return `${names[0]}–${names[names.length - 1]}`;
    }
    return names.filter(Boolean).join(', ');
  }

  private areConsecutiveWeekdays(ordered: number[]): boolean {
    const start = WEEKDAY_DISPLAY_ORDER.indexOf(ordered[0]);
    if (start < 0) return false;
    return ordered.every((day, index) => WEEKDAY_DISPLAY_ORDER[start + index] === day);
  }

  syncCellTooltip(event: Event, tooltip: MatTooltip, force = false): void {
    const el = event.currentTarget as HTMLElement;
    const truncated = force || el.scrollWidth > el.clientWidth;
    tooltip.disabled = !truncated;
    if (truncated) tooltip.show();
  }

  private targetNameList(alert: Alert): string[] {
    const names = alert.targets?.map((target) => target.name).filter(Boolean) ?? [];
    return names.length ? names : (alert.scope_name ? [alert.scope_name] : []);
  }

  private recipientList(alert: Alert): string[] {
    return alert.recipients?.map((recipient) => recipient.email).filter(Boolean) ?? [];
  }

  private matchesFilter(selected: Set<string>, values: string[]): boolean {
    if (!selected.size) return true;
    return values.some((value) => selected.has(value));
  }

  private uniqueSorted(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right, 'pt-BR'));
  }

  private load(): void {
    this.isLoading.set(true);
    this.alertsApi.listAlerts().subscribe({
      next: (alerts) => {
        this.alerts.set(alerts ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this.alerts.set([]);
        this.isLoading.set(false);
        this.toastr.show('Não foi possível carregar os alertas.', 'Erro', { timeOut: 5000, messageClass: 'error' });
      },
    });
  }
}
